import { useState, useMemo, useCallback, useEffect } from 'react';
import { GlobalItem, ItemCost } from '../../../api/protosCompiled/globalItem/globalItem_pb';
import { useFetchItemsByFileId } from '../../../hooks/useFetchItemsByFileId';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
  Tooltip,
  IconButton,
  Badge,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import CalculateIcon from '@mui/icons-material/Calculate';
import NotesIcon from '@mui/icons-material/Notes';
import { useUpdateItemCost } from '../../../hooks/useUpdateItemCost';
import React from 'react';
import LaborUnitCalculator from '../../laborUnit/LaborUnitCalculator';
import { LaborUnitCreatedEvent } from '../../../types/laborUnit.types';
import { calculateLaborUnit, DEFAULT_LABOR_CONFIG } from '../../../utils/laborUnit.utils';
import MaximizedTextEditor from '../../reports/MaximizedTextEditor';
import { SaveTemplateRequest } from '../../../types/template.types';

interface AssignCostsProps {
  itemId: string;
  onCostsUpdated?: () => void;
}

interface RowData {
  item: GlobalItem.AsObject;
  attribute: {
    attributevalueid: number;
    attributevaluename: string;
  };
  matchingCost: ItemCost.AsObject | undefined;
}

const AssignCosts: React.FC<AssignCostsProps> = ({ itemId, onCostsUpdated }) => {
  const theme = useTheme();
  const [editedCosts, setEditedCosts] = useState<{ [key: string]: any }>({});
  const [laborCalculatorOpen, setLaborCalculatorOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RowData | null>(null);
  const [originalValues, setOriginalValues] = useState<{
    [key: string]: { [field: string]: number };
  }>({});
  const [notesEditorOpen, setNotesEditorOpen] = useState(false);
  const [selectedNotesItem, setSelectedNotesItem] = useState<RowData | null>(null);

  const { data: items, isLoading } = useFetchItemsByFileId(itemId, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale to force refresh
  });
  const { mutate: updateItemCost } = useUpdateItemCost(itemId);

  useEffect(() => {
    console.log('items', items);
  }, [items]);

  useEffect(() => {
    // Clear edited costs when items data changes
    // This ensures we show fresh data after any update
    if (items) {
      setEditedCosts({});
    }
  }, [items]);

  // Create a flattened list of rows for virtualization
  const rows = useMemo<RowData[]>(() => {
    if (!items) return [];
    return items
      .sort((a, b) => a.itemname.localeCompare(b.itemname))
      .flatMap(item =>
        item.attributesList
          .sort((a, b) => a.attributevaluename.localeCompare(b.attributevaluename))
          .map(attribute => ({
            item,
            attribute,
            matchingCost: item.costsList.find(
              cost => cost.itemattributevalueid === attribute.attributevalueid
            ),
          }))
      );
  }, [items]);

  // Handle changes to the input fields
  const handleCostInputChange = useCallback((attributeId: string, field: string, value: string) => {
    // Only allow valid numeric input or empty input
    if (/^(\d+(\.\d*)?|\.\d*)?$/.test(value)) {
      setEditedCosts(prev => ({
        ...prev,
        [attributeId]: {
          ...prev[attributeId],
          [field]: value,
        },
      }));
    }
  }, []);

  // Handle focus to store original value
  const handleCostFocus = useCallback((attributeId: string, field: string, value: number) => {
    // Store the formatted value that the user sees, not the raw backend value
    const formattedValue =
      field === 'hourlyproductionrate'
        ? parseFloat(value.toFixed(3))
        : parseFloat(value.toFixed(2));

    setOriginalValues(prev => ({
      ...prev,
      [attributeId]: {
        ...prev[attributeId],
        [field]: formattedValue,
      },
    }));
  }, []);

  const handleLaborUnitApply = useCallback(
    (event: LaborUnitCreatedEvent) => {
      if (!selectedItem) return;

      const { calculatedUnit, percentages } = event;
      const { item, attribute } = selectedItem;

      const matchingCost = item.costsList.find(
        cost => cost.itemattributevalueid === attribute.attributevalueid
      );

      if (matchingCost) {
        const updatedCost: ItemCost.AsObject = {
          ...matchingCost,
          itemid: item.itemid,
          hourlyproductionrate: calculatedUnit.total,
          installationtime: calculatedUnit.installation,
          joblayouttime: percentages.layoutPct,
          materialcleanuptime: percentages.materialPct,
          nonproductivetime: percentages.nonProductivePct,
          toolhandlingtime: percentages.toolHandlingPct,
          notes: calculatedUnit.notes || '',
        };

        // Update the edited costs to reflect the new calculated value
        setEditedCosts(prev => ({
          ...prev,
          [attribute.attributevalueid]: {
            ...prev[attribute.attributevalueid],
            hourlyproductionrate: calculatedUnit.total.toFixed(3),
          },
        }));

        updateItemCost(updatedCost, {
          onSuccess: () => {
            if (onCostsUpdated) {
              onCostsUpdated();
            }
            setLaborCalculatorOpen(false);
            setSelectedItem(null);
          },
          onError: error => {
            console.error('Error updating item cost', error);
          },
        });
      }
    },
    [selectedItem, updateItemCost, onCostsUpdated]
  );

  // Helper function to calculate total hours from installation time and percentages
  const calculateTotalHours = useCallback((cost: ItemCost.AsObject) => {
    if (!cost.installationtime) return cost.hourlyproductionrate || 0;

    const spec = {
      installationHours: cost.installationtime,
      layoutPct: cost.joblayouttime,
      materialPct: cost.materialcleanuptime,
      nonProductivePct: cost.nonproductivetime,
      toolHandlingPct: cost.toolhandlingtime,
    };

    const result = calculateLaborUnit(spec);
    return result.total;
  }, []);

  // Handle notes editing
  const handleNotesClick = useCallback((rowData: RowData) => {
    setSelectedNotesItem(rowData);
    setNotesEditorOpen(true);
  }, []);

  const handleNotesChange = useCallback((notes: string) => {
    // Don't save immediately on change - just store the value
    // The save will happen when the dialog closes
  }, []);

  const handleNotesSave = useCallback(
    (notes: string) => {
      if (!selectedNotesItem) return;

      const { item, attribute } = selectedNotesItem;
      const matchingCost = item.costsList.find(
        cost => cost.itemattributevalueid === attribute.attributevalueid
      );

      if (matchingCost) {
        const updatedCost: ItemCost.AsObject = {
          ...matchingCost,
          itemid: item.itemid,
          notes: notes,
        };

        updateItemCost(updatedCost, {
          onSuccess: () => {
            if (onCostsUpdated) {
              onCostsUpdated();
            }
            // Close the dialog after successful save
            setNotesEditorOpen(false);
            setSelectedNotesItem(null);
          },
          onError: error => {
            console.error('Error updating item cost notes', error);
            // Keep dialog open on error so user can retry
          },
        });
      }
    },
    [selectedNotesItem, updateItemCost, onCostsUpdated]
  );

  const handleNotesEditorClose = useCallback(() => {
    setNotesEditorOpen(false);
    setSelectedNotesItem(null);
  }, []);

  //--------DO NOT CHANGE THIS handleCostBlur CODE EVER WITHOUT ASKING ME FIRST--------
  //--------Doing so can result in the customers saved cost data being overwritten.--------
  const handleCostBlur = async (item: GlobalItem.AsObject, attributeId: string, e: any) => {
    e.preventDefault();
    try {
      const matchingCost = item.costsList.find(
        cost => cost.itemattributevalueid === parseInt(attributeId)
      );
      if (matchingCost) {
        const editedCost: ItemCost.AsObject = editedCosts[attributeId] || {};

        // Get the current display values
        const currentHourlyRateDisplay =
          editedCost.hourlyproductionrate !== undefined
            ? editedCost.hourlyproductionrate.toString()
            : calculateTotalHours(matchingCost).toFixed(3);
        const currentUnitCostDisplay =
          editedCost.itemunitcost !== undefined
            ? editedCost.itemunitcost.toString()
            : matchingCost.itemunitcost.toFixed(2);

        // Parse the display values
        const parsedHourlyRate = parseFloat(currentHourlyRateDisplay);
        const parsedUnitCost = parseFloat(currentUnitCostDisplay);

        // Get original formatted values
        const originalValue = originalValues[attributeId] || {};
        const originalHourlyRate = originalValue.hourlyproductionrate;
        const originalUnitCost = originalValue.itemunitcost;

        // Compare the formatted values
        const hasHourlyRateChanged =
          originalHourlyRate !== undefined &&
          Math.abs(parsedHourlyRate - originalHourlyRate) > 0.0001;
        const hasUnitCostChanged =
          originalUnitCost !== undefined && Math.abs(parsedUnitCost - originalUnitCost) > 0.0001;

        // Only update if values have actually changed
        if (hasHourlyRateChanged || hasUnitCostChanged) {
          // Calculate installation time by working backwards from the total production time
          const percentageSum =
            DEFAULT_LABOR_CONFIG.layoutPct +
            DEFAULT_LABOR_CONFIG.materialPct +
            DEFAULT_LABOR_CONFIG.nonProductivePct +
            DEFAULT_LABOR_CONFIG.toolHandlingPct;

          const calculatedInstallationTime = parsedHourlyRate / (1 + percentageSum / 100);

          const updatedCost: ItemCost.AsObject = {
            ...matchingCost,
            itemid: item.itemid,
            itemunitcost: parsedUnitCost,
            hourlyproductionrate: parsedHourlyRate,
            installationtime: calculatedInstallationTime,
            joblayouttime: DEFAULT_LABOR_CONFIG.layoutPct,
            materialcleanuptime: DEFAULT_LABOR_CONFIG.materialPct,
            nonproductivetime: DEFAULT_LABOR_CONFIG.nonProductivePct,
            toolhandlingtime: DEFAULT_LABOR_CONFIG.toolHandlingPct,
          };

          // Format the displayed value
          setEditedCosts(prev => ({
            ...prev,
            [attributeId]: {
              ...prev[attributeId],
              hourlyproductionrate: parsedHourlyRate.toFixed(3),
              itemunitcost: parsedUnitCost.toFixed(2),
            },
          }));

          // Pass the updated cost object to updateItemCost react-query hook
          updateItemCost(updatedCost, {
            onSuccess: () => {
              if (onCostsUpdated) {
                onCostsUpdated();
              }
            },
            onError: error => {
              console.error('Error updating item cost', error);
            },
          });
        }

        // Clear the original value after handling blur
        setOriginalValues(prev => {
          const newState = { ...prev };
          delete newState[attributeId];
          return newState;
        });
      } else {
        console.error('No matching cost found for attributeId:', attributeId);
      }
    } catch (error) {
      console.error('Error updating item costs:', error);
    }
  };

  const handleSaveTemplate = useCallback(async (templateData: SaveTemplateRequest) => {
    try {
      // TODO: Implement actual template saving logic when backend is ready
      console.log('Template to save:', templateData);
    } catch (error) {
      console.error('Error saving template:', error);
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          height: '700px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
          borderRadius: 1,
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
          overflow: 'hidden',
        }}
        role="region"
        aria-label="Assign costs to items"
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2,
            }}
            role="status"
            aria-label="Loading items"
          >
            <CircularProgress size={60} color="primary" />
            <Typography variant="h6" color="text.secondary">
              Loading your items...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Table aria-label="Assign costs table header" role="grid">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width: '18%',
                    }}
                  >
                    Item Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width: '5%',
                    }}
                  >
                    UOM
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width: '20%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        justifyContent: 'center',
                      }}
                    >
                      <AttachMoneyIcon fontSize="small" aria-hidden="true" />
                      Unit Cost
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width: '20%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        justifyContent: 'center',
                      }}
                    >
                      <SpeedIcon fontSize="small" aria-hidden="true" />
                      Hours Per Unit
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      width: '10%',
                      textAlign: 'center',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box sx={{ overflow: 'auto', flex: 1 }}>
              <Table aria-label="Assign costs table content" role="grid">
                <TableBody>
                  {rows.map(({ item, attribute, matchingCost }) => {
                    const hasNotes = matchingCost?.notes && matchingCost.notes.trim().length > 0;

                    return (
                      <TableRow
                        key={`${item.itemid}-${attribute.attributevalueid}`}
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          height: '53px',
                          '&:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              theme.palette.mode === 'dark' ? 0.1 : 0.05
                            ),
                          },
                          '&:nth-of-type(even)': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? alpha(theme.palette.common.white, 0.02)
                                : alpha(theme.palette.common.black, 0.02),
                          },
                        }}
                        role="row"
                        aria-label={`Item: ${attribute.attributevaluename} - ${item.itemname}`}
                      >
                        <TableCell sx={{ color: theme.palette.text.primary }}>
                          <Tooltip
                            title={`${attribute.attributevaluename} - ${item.itemname}`}
                            arrow
                          >
                            <Typography noWrap>
                              {attribute.attributevaluename} - {item.itemname}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.primary }}>
                          {matchingCost?.uomname ?? 'N/A'}
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            name="itemunitcost"
                            value={
                              editedCosts[attribute.attributevalueid]?.itemunitcost ??
                              matchingCost?.itemunitcost?.toFixed(2) ??
                              ''
                            }
                            onChange={e =>
                              handleCostInputChange(
                                attribute.attributevalueid.toString(),
                                'itemunitcost',
                                e.target.value
                              )
                            }
                            onFocus={() =>
                              handleCostFocus(
                                attribute.attributevalueid.toString(),
                                'itemunitcost',
                                matchingCost?.itemunitcost || 0
                              )
                            }
                            onBlur={e =>
                              handleCostBlur(item, attribute.attributevalueid.toString(), e)
                            }
                            InputProps={{
                              sx: {
                                '& .MuiOutlinedInput-input': {
                                  color: theme.palette.text.primary,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.23)'
                                      : 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                              },
                            }}
                            aria-label={`Unit cost for ${attribute.attributevaluename}`}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            name="hourlyproductionrate"
                            value={
                              editedCosts[attribute.attributevalueid]?.hourlyproductionrate ??
                              (matchingCost ? calculateTotalHours(matchingCost).toFixed(3) : '')
                            }
                            onChange={e =>
                              handleCostInputChange(
                                attribute.attributevalueid.toString(),
                                'hourlyproductionrate',
                                e.target.value
                              )
                            }
                            onFocus={() =>
                              handleCostFocus(
                                attribute.attributevalueid.toString(),
                                'hourlyproductionrate',
                                matchingCost ? calculateTotalHours(matchingCost) : 0
                              )
                            }
                            onBlur={e =>
                              handleCostBlur(item, attribute.attributevalueid.toString(), e)
                            }
                            InputProps={{
                              sx: {
                                '& .MuiOutlinedInput-input': {
                                  color: theme.palette.text.primary,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.23)'
                                      : 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                },
                              },
                            }}
                            aria-label={`Hours per unit for ${attribute.attributevaluename}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Labor Unit Calculator" arrow>
                              <IconButton
                                onClick={() => {
                                  setLaborCalculatorOpen(true);
                                  setSelectedItem({
                                    item,
                                    attribute,
                                    matchingCost,
                                  });
                                }}
                                size="small"
                                color="primary"
                              >
                                <CalculateIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={hasNotes ? 'View/Edit Notes' : 'Add Notes'} arrow>
                              <Badge
                                color="primary"
                                variant="dot"
                                invisible={!hasNotes}
                                sx={{ '& .MuiBadge-dot': { right: 2, top: 2 } }}
                              >
                                <IconButton
                                  onClick={() =>
                                    handleNotesClick({ item, attribute, matchingCost })
                                  }
                                  size="small"
                                  sx={{
                                    color: hasNotes
                                      ? theme.palette.primary.main
                                      : theme.palette.action.active,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    },
                                  }}
                                >
                                  <NotesIcon />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </Box>

      {/* Labor Unit Calculator dialog */}
      {selectedItem && (
        <LaborUnitCalculator
          open={laborCalculatorOpen}
          onClose={() => {
            setLaborCalculatorOpen(false);
            setSelectedItem(null);
          }}
          onApply={handleLaborUnitApply}
          itemName={`${selectedItem.attribute.attributevaluename} - ${selectedItem.item.itemname}`}
          uom={selectedItem.matchingCost?.uomname ?? 'N/A'}
          uomValue={selectedItem.matchingCost?.uomvalue?.toString() ?? 'N/A'}
          defaultTotalHours={selectedItem.matchingCost?.hourlyproductionrate ?? 1.0}
          existingData={
            selectedItem.matchingCost
              ? {
                  installation:
                    selectedItem.matchingCost.installationtime ||
                    selectedItem.matchingCost.hourlyproductionrate /
                      (1 +
                        (selectedItem.matchingCost.joblayouttime +
                          selectedItem.matchingCost.materialcleanuptime +
                          selectedItem.matchingCost.nonproductivetime +
                          selectedItem.matchingCost.toolhandlingtime) /
                          100),
                  layoutPct: selectedItem.matchingCost.joblayouttime,
                  materialPct: selectedItem.matchingCost.materialcleanuptime,
                  nonProductivePct: selectedItem.matchingCost.nonproductivetime,
                  toolHandlingPct: selectedItem.matchingCost.toolhandlingtime,
                  notes: selectedItem.matchingCost.notes,
                }
              : undefined
          }
        />
      )}

      {/* Notes Editor dialog */}
      {selectedNotesItem && (
        <MaximizedTextEditor
          open={notesEditorOpen}
          onClose={handleNotesEditorClose}
          value={selectedNotesItem.matchingCost?.notes || ''}
          onChange={handleNotesChange}
          onSave={handleNotesSave}
          title={`Notes - ${selectedNotesItem.attribute.attributevaluename} - ${selectedNotesItem.item.itemname}`}
          placeholder={`
          🧠 Think thru each of the five labor slices and add notes for each... \n
          - Installation \n
          - Layout \n
          - Material Cleanup \n
          - Non-Productive Time \n
          - Tool Handling \n
          `}
          doneButtonText="Save Notes"
          enableTemplates={true}
          suggestedTemplateType="labor-unit-notes"
          maxWidth="lg"
          maxHeight="80vh"
          userTemplates={[]}
          onSaveTemplate={handleSaveTemplate}
          templateVariables={{
            itemName: `${selectedNotesItem.attribute.attributevaluename} - ${selectedNotesItem.item.itemname}`,
            uom: selectedNotesItem.matchingCost?.uomname ?? 'Could not find UOM name',
            uomValue:
              selectedNotesItem.matchingCost?.uomvalue?.toString() ?? 'Could not find UOM value',
          }}
          onTemplateLoad={templateId => {
            console.log('Loading template with variables:', {
              itemName: `${selectedNotesItem.attribute.attributevaluename} - ${selectedNotesItem.item.itemname}`,
              uom: selectedNotesItem.matchingCost?.uomname,
              uomValue: selectedNotesItem.matchingCost?.uomvalue,
            });
          }}
        />
      )}
    </>
  );
};

export default React.memo(AssignCosts);
